import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useFeaturesStore } from './features'

export type Theme = 'light' | 'dark'

export const useThemeStore = defineStore('theme', () => {
  // Theme state
  const currentTheme = ref<Theme>('dark')
  
  // Get features store to check if light mode is disabled
  const featuresStore = useFeaturesStore()
  
  // Computed properties
  const isDarkMode = computed(() => currentTheme.value === 'dark')
  const isLightMode = computed(() => currentTheme.value === 'light')
  
  // Check if light mode is disabled by admin
  const isLightModeDisabled = computed(() => featuresStore.isLightModeDisabled)
  
  // Actions
  const setTheme = (theme: Theme) => {
    // Prevent switching to light mode if disabled by admin
    if (theme === 'light' && isLightModeDisabled.value) {
      console.warn('Light mode is disabled by admin')
      return
    }
    
    currentTheme.value = theme
    applyTheme(theme)
    saveToLocalStorage()
  }
  
  const toggleTheme = () => {
    // If light mode is disabled, don't allow toggle to light mode
    if (isLightModeDisabled.value && currentTheme.value === 'dark') {
      console.warn('Light mode is disabled by admin')
      return
    }
    
    const newTheme = currentTheme.value === 'dark' ? 'light' : 'dark'
    setTheme(newTheme)
  }
  
  // Apply theme to document
  const applyTheme = (theme: Theme) => {
    const html = document.documentElement
    
    // Remove existing theme classes
    html.classList.remove('light-theme', 'dark-theme')
    
    // Add new theme class
    html.classList.add(`${theme}-theme`)
    
    // Set data attribute for CSS targeting
    html.setAttribute('data-theme', theme)
  }
  
  // Load theme from localStorage
  const loadFromLocalStorage = () => {
    const savedTheme = localStorage.getItem('theme') as Theme
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      currentTheme.value = savedTheme
      applyTheme(savedTheme)
    } else {
      // Default to dark theme
      applyTheme('dark')
    }
  }
  
  // Save theme to localStorage
  const saveToLocalStorage = () => {
    localStorage.setItem('theme', currentTheme.value)
  }
  
  // Initialize theme on store creation
  loadFromLocalStorage()
  
  return {
    // State
    currentTheme,
    
    // Computed
    isDarkMode,
    isLightMode,
    isLightModeDisabled,
    
    // Actions
    setTheme,
    toggleTheme,
    applyTheme,
    loadFromLocalStorage
  }
})
